import { useState, useEffect } from 'react';
import { History, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ActivityTimeline } from './ActivityTimeline';
import { ActivityLogFilters } from './ActivityLogFilters';
import { useActivityLogs } from './useActivityLogs';
import { ActivityLogFilters as Filters } from './types';

interface ActivityLogPanelProps {
  entityType?: string;
  entityId?: string;
  title?: string;
  showFilters?: boolean;
  showEntityBadge?: boolean;
  maxHeight?: string;
  limit?: number;
  className?: string;
}

export function ActivityLogPanel({
  entityType,
  entityId,
  title = 'Activity Log',
  showFilters = true,
  showEntityBadge = !entityType, // Hide entity badge if filtering by entity type
  maxHeight = '600px',
  limit = 100,
  className = '',
}: ActivityLogPanelProps) {
  const [filters, setFilters] = useState<Filters>({
    entityType,
    entityId,
  });

  // Update filters when props change
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      entityType,
      entityId,
    }));
  }, [entityType, entityId]);

  const { logs, loading, refetch } = useActivityLogs({
    filters,
    limit,
    realtime: true,
  });

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5" />
            {title}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {showFilters && (
          <div className="mb-4">
            <ActivityLogFilters
              filters={filters}
              onFiltersChange={setFilters}
              showEntityFilter={!entityType}
            />
          </div>
        )}
        <ScrollArea style={{ maxHeight }} className="pr-4">
          <ActivityTimeline
            logs={logs}
            loading={loading}
            showEntityBadge={showEntityBadge}
          />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
